// ---------------------------------------------------------------
// Dispatch board storage: loads / freight bills in IndexedDB.
// Data model is kept flat and clean so it can move to the server later.
// ---------------------------------------------------------------

const DB_NAME = 'dynamic-dispatch';
const STORE = 'loads';
const META = 'load-meta';
const PARTIES = 'parties';

// One place that knows every object store. A cached connection that steps
// aside when a newer version wants to upgrade, so open order never matters
// and a version bump never gets blocked by a stale connection.
let _dbPromise = null;
export function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 7);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('vehicle-photos')) db.createObjectStore('vehicle-photos');
      if (!db.objectStoreNames.contains('driver-files')) db.createObjectStore('driver-files');
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
      if (!db.objectStoreNames.contains(PARTIES)) db.createObjectStore(PARTIES, { keyPath: 'key' });
      if (!db.objectStoreNames.contains('trailers')) db.createObjectStore('trailers', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('reports')) db.createObjectStore('reports', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('trips')) db.createObjectStore('trips', { keyPath: 'id' });
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => { db.close(); _dbPromise = null; };
      resolve(db);
    };
    req.onerror = () => { _dbPromise = null; reject(req.error); };
    req.onblocked = () => { _dbPromise = null; };
  });
  return _dbPromise;
}

function tx(store, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const os = t.objectStore(store);
    const r = fn(os);
    t.oncomplete = () => resolve(r && r.result !== undefined ? r.result : r);
    t.onerror = () => reject(t.error);
  }));
}

// ---- bill-number settings (prefix + next number) ----
const DEFAULT_SETTINGS = { prefix: 'DD-', next: 1001 };

export async function getBillSettings() {
  const s = await tx(META, 'readonly', (os) => os.get('billSettings'));
  return s || DEFAULT_SETTINGS;
}
export async function saveBillSettings(settings) {
  await tx(META, 'readwrite', (os) => os.put(settings, 'billSettings'));
}

// ---- detention settings ----
const DEFAULT_DETENTION = { freeHours: 3, ratePerHour: 50, radiusMeters: 300, autoTrack: true };
export async function getDetentionSettings() {
  const s = await tx(META, 'readonly', (os) => os.get('detentionSettings'));
  return { ...DEFAULT_DETENTION, ...(s || {}) };
}
export async function saveDetentionSettings(settings) {
  await tx(META, 'readwrite', (os) => os.put(settings, 'detentionSettings'));
}

async function nextBillNumber() {
  const s = await getBillSettings();
  const number = `${s.prefix}${s.next}`;
  await saveBillSettings({ ...s, next: s.next + 1 });
  return number;
}

// ---- trip-number settings (system-generated, assigned on dispatch) ----
const DEFAULT_TRIP_SETTINGS = { prefix: '', next: 700001 };
export async function getTripSettings() {
  const s = await tx(META, 'readonly', (os) => os.get('tripSettings'));
  return s || DEFAULT_TRIP_SETTINGS;
}
export async function saveTripSettings(settings) {
  await tx(META, 'readwrite', (os) => os.put(settings, 'tripSettings'));
}
export async function nextTripNumber() {
  const s = await getTripSettings();
  const number = `${s.prefix}${s.next}`;
  await saveTripSettings({ ...s, next: s.next + 1 });
  return number;
}

// ---- loads CRUD ----
export async function getLoads() {
  const all = await tx(STORE, 'readonly', (os) => {
    const out = [];
    return new Promise((res) => {
      os.openCursor().onsuccess = (e) => {
        const cur = e.target.result;
        if (cur) { out.push(cur.value); cur.continue(); }
        else res(out);
      };
    });
  });
  return all.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export function emptyStop(type) {
  return {
    id: `S${Date.now()}${Math.floor(Math.random() * 1000)}`,
    type, // 'pickup' | 'delivery'
    name: '', account: '', address: '', city: '', state: '', zip: '', phone: '', contact: '',
    apptReq: true, apptType: 'By appointment', early: '', late: '',
    refNumber: '', commodity: '', weight: '', pieces: '', notes: '',
  };
}

export function emptyLoad() {
  return {
    id: null,
    billNumber: null,
    tripId: null, // the execution plan this load is currently on (created on assignment)
    tripHistory: [], // [{ tripId, tripNumber, from, to, at }] — every trip/leg the load moved through
    status: 'Available',
    createdBy: '',
    createdAt: null,
    // broker / bill-to party
    caller: { name: '', account: '', address: '', city: '', state: '', zip: '', phone: '', contact: '' },
    billTo: 'Caller',
    // stops — any number of pickups and deliveries, in order
    stops: [emptyStop('pickup'), emptyStop('delivery')],
    // derived from stops on save (kept for board / geofence / directory compatibility)
    shipper: { name: '', account: '', address: '', city: '', state: '', zip: '', phone: '', contact: '' },
    consignee: { name: '', account: '', address: '', city: '', state: '', zip: '', phone: '', contact: '' },
    pickup: { apptReq: true, early: '', late: '', zone: '' },
    delivery: { apptReq: true, early: '', late: '', zone: '' },
    miles: '',
    // load
    pieces: '', commodity: '', description: '', weight: '', cube: '', declaredValue: '',
    // references
    references: [], // { type: 'Pickup#'|'PO'|'BOL'|'Ref', number: '' }
    // charges
    charges: [], // { code, description, qty, rate }  amount = qty*rate
    invoiceType: 'FREIGHT', txType: 'INVOICE',
    // billing module (tabbed screen): cost/settlement/documents/invoice/AR
    billing: {}, // { invoiceNumber, invoiceDate, dueDate, terms, poNumber, status, salesRep, branch, paymentsReceived, disputeStatus, collectionNotes }
    costs: [], // { type, vendor, amount, payStatus }
    settlement: {}, // { milesPaid, cpm, stopPay, detentionPay, layoverPay, bonuses, advances, deductions }
    documents: {}, // { rateCon, bol, pod, lumperReceipt, ... } booleans
    // assignment
    vehicleId: '', driverId: '', tripNumber: '',
    notes: '',
    // dispatch workflow
    statusHistory: [], // [{ status, at }]
    pod: null, // proof-of-delivery note/signature captured at completion
    // automation (geofence tracking + detention)
    autoTrack: true,
    geo: { pickupId: null, deliveryId: null, pickupLat: null, pickupLng: null, deliveryLat: null, deliveryLng: null, armed: false, lastEventId: 0 },
    track: { pickupArrival: null, pickupDeparture: null, deliveryArrival: null, deliveryDeparture: null,
      pickupDetentionHrs: 0, deliveryDetentionHrs: 0 },
  };
}

// Keep shipper/consignee/pickup/delivery in sync with the stops list so the
// board, geofence automation and party directory keep working unchanged.
export function syncStops(load) {
  const stops = load.stops && load.stops.length ? load.stops : null;
  if (!stops) return load;
  const pickups = stops.filter((s) => s.type === 'pickup');
  const deliveries = stops.filter((s) => s.type === 'delivery');
  const fp = pickups[0] || {};
  const ld = deliveries[deliveries.length - 1] || {};
  const partyOf = (s) => ({ name: s.name || '', account: s.account || '', address: s.address || '', city: s.city || '', state: s.state || '', zip: s.zip || '', phone: s.phone || '', contact: s.contact || '' });
  return {
    ...load,
    shipper: partyOf(fp),
    consignee: partyOf(ld),
    pickup: { apptReq: !!fp.apptReq, early: fp.early || '', late: fp.late || '', zone: fp.zip || '' },
    delivery: { apptReq: !!ld.apptReq, early: ld.early || '', late: ld.late || '', zone: ld.zip || '' },
  };
}

// Old loads (pre multi-stop) get a stops array synthesized from shipper/consignee.
export function ensureStops(load) {
  if (load.stops && load.stops.length) return load;
  const mk = (type, party, sched) => ({
    ...emptyStop(type),
    name: party.name || '', account: party.account || '', address: party.address || '',
    city: party.city || '', state: party.state || '', zip: party.zip || (sched && sched.zone) || '',
    phone: party.phone || '', contact: party.contact || '',
    apptReq: sched ? !!sched.apptReq : true, early: (sched && sched.early) || '', late: (sched && sched.late) || '',
  });
  return { ...load, stops: [mk('pickup', load.shipper || {}, load.pickup), mk('delivery', load.consignee || {}, load.delivery)] };
}

export async function saveLoad(load) {
  const rec = { ...load };
  if (!rec.id) {
    rec.id = `L${Date.now()}`;
    rec.billNumber = rec.billNumber || (await nextBillNumber());
    rec.createdAt = new Date().toISOString();
  }
  await tx(STORE, 'readwrite', (os) => os.put(rec));
  return rec;
}

export async function deleteLoad(id) {
  await tx(STORE, 'readwrite', (os) => os.delete(id));
}

// low-level put used by the automation watcher (no bill-number side effects)
export async function putLoad(load) {
  await tx(STORE, 'readwrite', (os) => os.put(load));
}

// change a load's status and record it on the timeline
export function withStatus(load, status, note) {
  if (load.status === status) return load;
  const entry = { status, at: new Date().toISOString() };
  if (note) entry.note = note;
  return { ...load, status, statusHistory: [...(load.statusHistory || []), entry] };
}

export const chargeAmount = (c) => (Number(c.qty) || 0) * (Number(c.rate) || 0);
export const loadTotal = (load) => (load.charges || []).reduce((s, c) => s + chargeAmount(c), 0);

// ---- party directory (saved callers / shippers / consignees) ----
const partyKey = (p) => (p.account && p.account.trim()) ? `acct:${p.account.trim().toLowerCase()}` : (p.name ? `name:${p.name.trim().toLowerCase()}` : null);

export async function getParties() {
  return tx(PARTIES, 'readonly', (os) => {
    const out = [];
    return new Promise((res) => {
      os.openCursor().onsuccess = (e) => {
        const cur = e.target.result;
        if (cur) { out.push(cur.value); cur.continue(); }
        else res(out.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      };
    });
  });
}

// Save/update a party in the directory (called automatically when a load is saved)
export async function rememberParty(p) {
  const key = partyKey(p);
  if (!key) return;
  const record = { key, name: p.name || '', account: p.account || '', address: p.address || '', city: p.city || '', state: p.state || '', zip: p.zip || '', phone: p.phone || '', contact: p.contact || '' };
  await tx(PARTIES, 'readwrite', (os) => os.put(record));
}

export async function findPartyByAccount(account) {
  if (!account || !account.trim()) return null;
  const rec = await tx(PARTIES, 'readonly', (os) => os.get(`acct:${account.trim().toLowerCase()}`));
  return rec || null;
}

export const LOAD_STATUSES = [
  'Available', 'Dispatched', 'At Pickup', 'Loaded', 'In Transit', 'At Delivery', 'Delivered', 'Complete', 'Invoiced',
];

export const STATUS_COLORS = {
  Available: '#64748b', Dispatched: '#0066ff', 'At Pickup': '#8b5cf6', Loaded: '#0891b2',
  'In Transit': '#f59e0b', 'At Delivery': '#8b5cf6', Delivered: '#22c55e', Complete: '#15803d', Invoiced: '#0f172a',
};
